import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaArrowRight, FaPhone, FaWhatsapp } from 'react-icons/fa';
import Container from '../components/ui/Container';
import FAQAccordion from '../components/ui/FAQAccordion';
import CTASection from '../components/ui/CTASection';
import ContactForm from '../components/ui/ContactForm';
import PageHero from '../components/ui/PageHero';
import { serviceCategories } from '../data/services';
import { contactInfo } from '../data/content';

function findService(slug) {
  for (const cat of serviceCategories) {
    const svc = cat.services.find(s => s.slug === slug);
    if (svc) return { service: svc, category: cat };
  }
  return null;
}

const defaultBenefits = [
  'Expert guidance from qualified professionals',
  'End-to-end documentation support',
  'Fast and hassle-free processing',
  'Transparent pricing with no hidden charges',
  'Regular status updates throughout the process',
  'Post-registration compliance support',
];

const defaultDocuments = [
  'PAN Card (Individual/Company)',
  'Aadhaar Card / Passport',
  'Address Proof (recent utility bill)',
  'Passport-size photographs',
  'Bank account details',
  'Business address proof',
];

const defaultProcess = [
  { step: '01', title: 'Consultation', desc: 'Share your requirement with our expert for a free initial assessment.' },
  { step: '02', title: 'Document Collection', desc: 'We provide a clear checklist and collect all required documents securely.' },
  { step: '03', title: 'Application Filing', desc: 'Our experts prepare and file your application with the relevant authority.' },
  { step: '04', title: 'Follow-up', desc: 'We track your application and respond to any queries from authorities.' },
  { step: '05', title: 'Certificate Delivery', desc: 'Receive your certificate and guidance on subsequent compliance steps.' },
];

const defaultFAQs = [
  { q: 'How long does this process take?', a: 'The timeline varies based on the service. Please contact us for a specific estimate after reviewing your documents.' },
  { q: 'What are your fees?', a: 'Our fees are transparent and competitive. We provide a detailed quote upfront after understanding your requirements.' },
  { q: 'Do you provide support after registration?', a: 'Yes, we provide comprehensive post-registration compliance support to ensure ongoing statutory compliance.' },
  { q: 'Can I track the status of my application?', a: 'Absolutely. We provide regular status updates via WhatsApp and email throughout the entire process.' },
];

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const found = findService(slug);

  if (!found) {
    return (
      <main className="w-full pt-20 pb-16 text-center">
        <Container>
          <h1 className="text-3xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Service Not Found</h1>
          <p className="mb-6" style={{ color: 'var(--color-text-muted)' }}>The service you're looking for doesn't exist.</p>
          <Link to="/services" className="btn-primary inline-flex items-center gap-2">
            ← Back to Services
          </Link>
        </Container>
      </main>
    );
  }

  const { service, category } = found;

  return (
    <main className="w-full overflow-x-hidden pt-16 lg:pt-20">
      {/* Banner */}
      <PageHero
        badge={category.title}
        title={service.title}
        subtitle={`${service.description} Our expert team ensures a seamless, compliant, and stress-free experience from start to finish.`}
        centered={false}
        breadcrumb={
          <>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white transition-colors">Services</Link>
            <span>/</span>
            <span className="text-white">{service.title}</span>
          </>
        }
      >
        <div className="flex flex-wrap gap-4">
          <a href="/contact" className="btn-primary">Get Started Today</a>
          <a
            href={`https://wa.me/${contactInfo.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white border border-white/30 hover:bg-white/10 transition-colors"
          >
            <FaWhatsapp className="text-green-400 text-lg" /> WhatsApp Us
          </a>
        </div>
      </PageHero>

      {/* Main Content */}
      <section className="section-pad section-bg-alt">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Service Overview</h2>
                <div className="card p-6">
                  <p className="text-base mb-4" style={{ color: 'var(--color-text-muted)' }}>
                    {service.description} This service is designed to ensure full compliance with applicable laws, regulations, and government requirements. Our team of qualified professionals handles every aspect of the process, so you can focus on running your business.
                  </p>
                  <p className="text-base" style={{ color: 'var(--color-text-muted)' }}>
                    We provide complete documentation support, application preparation, filing, follow-up with authorities, and post-registration compliance guidance. Our transparent process keeps you informed at every step.
                  </p>
                </div>
              </motion.div>

              {/* Benefits */}
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Key Benefits</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {defaultBenefits.map((b, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.07 }}
                      className="flex items-start gap-3 card p-4"
                    >
                      <FaCheckCircle className="flex-shrink-0 mt-0.5" style={{ color: 'var(--color-success)' }} />
                      <span className="text-sm" style={{ color: 'var(--color-text)' }}>{b}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Required Documents */}
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Required Documents</h2>
                <div className="card p-6">
                  <ul className="space-y-3">
                    {defaultDocuments.map((doc, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm pb-3 last:pb-0 border-b last:border-0" style={{ color: 'var(--color-text)', borderColor: 'var(--color-border-light)' }}>
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white" style={{ background: 'var(--color-primary)' }}>
                          {i + 1}
                        </span>
                        {doc}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              {/* Process Steps */}
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-text)' }}>Our Process</h2>
                <div className="space-y-4">
                  {defaultProcess.map((p, i) => (
                    <motion.div
                      key={p.step}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="card p-5 flex gap-5"
                    >
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0 text-white"
                        style={{ background: 'var(--gradient-primary)' }}
                      >
                        {p.step}
                      </div>
                      <div>
                        <h4 className="font-bold mb-1" style={{ color: 'var(--color-text)' }}>{p.title}</h4>
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{p.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* FAQ */}
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-text)' }}>Frequently Asked Questions</h2>
                <FAQAccordion items={defaultFAQs} />
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Enquiry */}
              <div className="card p-6 sticky top-28">
                <h3 className="font-bold text-lg mb-4" style={{ color: 'var(--color-text)' }}>Quick Enquiry</h3>
                <ContactForm />
              </div>

              {/* Contact card */}
              <div className="card p-6" style={{ background: 'var(--gradient-primary)' }}>
                <h3 className="text-white font-bold mb-3">Need Immediate Help?</h3>
                <p className="text-white/70 text-sm mb-4">Call or WhatsApp us now for instant assistance.</p>
                <div className="space-y-3">
                  <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-3 text-white text-sm font-medium bg-white/10 hover:bg-white/20 transition-colors rounded-lg px-4 py-3">
                    <FaPhone style={{ color: 'var(--color-secondary)' }} /> {contactInfo.phone}
                  </a>
                  <a href={`https://wa.me/${contactInfo.whatsapp}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 text-white text-sm font-medium rounded-lg px-4 py-3" style={{ background: '#25D366' }}>
                    <FaWhatsapp className="text-lg" /> WhatsApp Us
                  </a>
                </div>
              </div>

              {/* Other services */}
              <div className="card p-6">
                <h3 className="font-bold mb-4" style={{ color: 'var(--color-text)' }}>Related Services</h3>
                <ul className="space-y-2">
                  {category.services.slice(0, 6).filter(s => s.slug !== slug).slice(0, 5).map(s => (
                    <li key={s.id}>
                      <Link
                        to={`/services/${s.slug}`}
                        className="flex items-center gap-2 text-sm hover:gap-3 transition-all duration-200 py-1"
                        style={{ color: 'var(--color-primary)' }}
                      >
                        <FaArrowRight className="text-xs" /> {s.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <CTASection title={`Ready to Get ${service.title}?`} subtitle="Our experts are ready to help. Get started with a free consultation today." />
    </main>
  );
}
